import { useEffect, useState } from 'preact/hooks';
import { getBalance, requestPayout, type BalanceResponse, CACHE_KEYS } from '../lib/api';
import { getCached } from '../lib/cache';
import { Skeleton } from '../components/Skeleton';
import { haptic } from '../lib/telegram';

const MONTH_ES_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function fmtNum(n: number): string {
  return Math.round(n).toLocaleString('es-DO');
}

function fmtShortDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso || '';
  const day = parseInt(m[3], 10);
  const mon = MONTH_ES_SHORT[parseInt(m[2], 10) - 1] || '';
  return `${day} ${mon}`;
}

export function Pagos() {
  const [data, setData] = useState<BalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestingPayout, setRequestingPayout] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setError(null);
    const cached = getCached<BalanceResponse>(CACHE_KEYS.balance);
    if (cached) {
      setData(cached.value);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const r = await getBalance();
      setData(r);
    } catch (e) {
      if (!cached) setError((e as Error).message || 'No pudimos cargar tu saldo');
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestPayout() {
    if (!data || data.neto <= 0 || requestingPayout) return;
    if (!confirm(`¿Solicitar pago de RD$ ${fmtNum(data.neto)}? Angie revisará y confirmará el pago.`)) return;

    setRequestingPayout(true);
    haptic('medium');
    try {
      const result = await requestPayout();
      haptic('success');
      // Refresh balance to reflect new pending payout
      await load();
      alert(`✓ Solicitud enviada · RD$ ${fmtNum(result.monto || data.neto)}\n\nAngie recibirá la solicitud y confirmará el pago.`);
    } catch (e) {
      const msg = (e as Error).message;
      haptic('error');
      if (msg === 'pending_payout_exists') {
        alert('Ya tienes una solicitud pendiente. Espera a que Angie la procese.');
        await load();
      } else if (msg === 'no_balance') {
        alert('No tienes saldo por cobrar.');
      } else if (msg === 'saldo_no_verificado') {
        alert('No pudimos verificar tu saldo en este momento. Intenta de nuevo en unos minutos.');
      } else {
        alert(`No pudimos enviar la solicitud: ${msg}`);
      }
    } finally {
      setRequestingPayout(false);
    }
  }

  if (loading) {
    return (
      <div class="screen">
        <header class="app-header"><h1>Pagos</h1></header>
        <div class="saldo-card">
          <Skeleton width={130} height={12} style={{ margin: '0 auto 10px' }} />
          <Skeleton width={180} height={28} style={{ margin: '0 auto' }} />
        </div>
        <div class="section-header">Detalle por evento</div>
        <div class="card">
          {[0, 1, 2].map((i) => (
            <div key={i} class="ledger-row">
              <div class="ledger-info" style={{ flex: 1 }}>
                <Skeleton width="60%" height={14} style={{ marginBottom: 6 }} />
                <Skeleton width="40%" height={12} />
              </div>
              <Skeleton width={80} height={14} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-screen">
        <h2>No pudimos cargar</h2>
        <p>{error}</p>
        <button class="btn primary" onClick={load}>Reintentar</button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div class="screen">
      <header class="app-header">
        <h1>Pagos</h1>
      </header>

      <div class="saldo-card">
        <div class="saldo-lbl">Saldo por cobrar</div>
        <div class="saldo-amt">RD$ {fmtNum(data.neto)}</div>
        {data.penalidad > 0 && (
          <div class="saldo-sub">
            Tarifa RD$ {fmtNum(data.saldo)} − deducciones RD$ {fmtNum(data.penalidad)}
          </div>
        )}
        {data.pendingPayout ? (
          <div class="saldo-pill warning">
            ⏳ Solicitud pendiente: RD$ {fmtNum(data.pendingPayout.monto)}
            {data.pendingPayout.fecha && ` (${fmtShortDate(data.pendingPayout.fecha)})`}
          </div>
        ) : data.neto > 0 ? (
          <button
            class="btn primary"
            style={{ marginTop: 12, maxWidth: 280 }}
            disabled={requestingPayout}
            onClick={handleRequestPayout}
          >
            {requestingPayout ? 'Enviando...' : `Solicitar pago de RD$ ${fmtNum(data.neto)}`}
          </button>
        ) : null}
        {data.ultimoPago && (
          <div class="saldo-foot">Último pago: {fmtShortDate(data.ultimoPago)}</div>
        )}
      </div>

      {data.recentEvents.length > 0 && (
        <>
          <div class="section-header">Detalle por evento</div>
          <div class="card">
            {data.recentEvents.map((e) => (
              <div class="ledger-row" key={e.eventoId}>
                <div class="ledger-info">
                  <div class="lt-title">{e.cliente || e.eventoId}</div>
                  <div class="lt-sub">
                    {fmtShortDate(e.fecha)}
                    {e.rol && ` · ${e.rol}`}
                    {e.pendienteCobro && (
                      <span style={{ color: 'var(--warning, #b45309)' }}>
                        {' · ⏳ se acredita al confirmarse el pago del cliente'}
                      </span>
                    )}
                  </div>
                </div>
                <div
                  class={`ledger-amt ${e.pendienteCobro ? '' : 'amt-pos'}`}
                  style={e.pendienteCobro ? { opacity: 0.55 } : undefined}
                >
                  +RD$ {fmtNum(e.total || (e.salario + e.propinas))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {data.activePenalties.length > 0 && (
        <>
          <div class="section-header">Deducciones pendientes</div>
          <div class="card">
            {data.activePenalties.map((p) => (
              <div class="ledger-row" key={p.id}>
                <div class="ledger-info">
                  <div class="lt-title">
                    {p.tipo === 'Strike' ? '⚠️ Strike' : p.tipo === 'Cargo' ? '🧾 Cargo por uniforme' : '⚠️ Multa'}
                    {p.tipo !== 'Cargo' && p.motivo && ` — ${p.motivo}`}
                  </div>
                  <div class="lt-sub">
                    {fmtShortDate(p.fecha)}
                    {p.eventoId && ` · ${p.eventoId}`}
                  </div>
                </div>
                {p.tipo !== 'Strike' && (
                  <div class="ledger-amt amt-neg">−RD$ {fmtNum(p.saldoPendiente || p.monto)}</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {data.recentPayouts.length > 0 && (
        <>
          <div class="section-header">Histórico de pagos</div>
          <div class="card">
            {data.recentPayouts.map((p) => (
              <div class="ledger-row" key={p.id}>
                <div class="ledger-info">
                  <div class="lt-title">Pago {p.id}</div>
                  <div class="lt-sub">{fmtShortDate(p.fecha)}</div>
                </div>
                <div class="ledger-amt amt-pos">RD$ {fmtNum(p.monto)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {data.recentEvents.length === 0 && data.activePenalties.length === 0 && data.recentPayouts.length === 0 && (
        <div class="empty">
          <div class="ic">💵</div>
          <p>Aún no hay actividad financiera para mostrar.</p>
        </div>
      )}
    </div>
  );
}
